import { useLayoutEffect, useState } from 'react'
import type { RefObject } from 'react'

type Size = { width: number; height: number }

/**
 * The element's size (its padding box, as clientWidth/Height measure), kept
 * current as it resizes. Apex fixes a
 * chart's size when drawing, so charts that fill a flexible slot are given
 * this in px.
 */
export const useElementSize = (ref: RefObject<HTMLElement | null>): Size => {
  const [size, setSize] = useState<Size>({ width: 0, height: 0 })

  useLayoutEffect(() => {
    const element = ref.current
    if (!element) return
    const measure = () => {
      const { clientWidth: width, clientHeight: height } = element
      // The same object keeps React from re-rendering on a no-op resize.
      setSize(current =>
        current.width === width && current.height === height
          ? current
          : { width, height }
      )
    }
    measure()
    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(measure)
    observer.observe(element)
    return () => observer.disconnect()
  }, [ref])

  return size
}
